class ImageMagnifier {
  constructor(options = {}) {
    this.zoom = options.zoom || 2;
    this.borderWidth = options.borderWidth || 3;
    this.glassSize = options.glassSize || 100;
    this.initialized = new Set(); // Track which images have been initialized
    this.resizeHandlers = new Map(); // Track resize handlers for cleanup
    this.firstMoveHandler = null;
  }

  init(selector = '.splide--product image-with-placeholder') {
    // Clean up any existing magnifiers first
    this.cleanup();
    
    const containers = document.querySelectorAll(selector);
    
    containers.forEach(container => {
      // Prevent event bubbling on container clicks
      container.addEventListener('click', e => e.stopPropagation());
      
      const img = container.querySelector('img');
      if (!img || this.initialized.has(img)) return;
      
      this.setupMagnifier(img, container);
      this.initialized.add(img);
    });

    // Set up the first mouse move handler
    this.setupFirstMoveHandler();
  }

  setupMagnifier(img, container) {
    // Only setup magnifier if image is loaded
    if (!img.complete || !img.naturalWidth) {
      // Wait for image to load before setting up magnifier
      const loadHandler = () => {
        img.removeEventListener('load', loadHandler);
        this.setupMagnifier(img, container);
      };
      img.addEventListener('load', loadHandler);
      return;
    }

    // Create magnifier glass
    const glass = this.createMagnifierGlass(img);
    container.insertBefore(glass, img);

    // Set up event handlers
    const moveMagnifier = (e) => this.moveMagnifier(e, img, glass);
    const recalcMagnifier = () => this.recalculateMagnifier(img, glass);

    // Mouse and touch events
    glass.addEventListener("mousemove", moveMagnifier, { passive: false });
    img.addEventListener("mousemove", moveMagnifier, { passive: false });
    glass.addEventListener("touchmove", moveMagnifier, { passive: false });
    img.addEventListener("touchmove", moveMagnifier, { passive: false });

    // Hide cursor on image and glass
    img.style.cursor = 'none';
    glass.style.cursor = 'none';

    // Hover events for show/hide
    container.addEventListener('mouseenter', () => {
      this.showMagnifier(glass);
      // Hide cursor when magnifier is active
      const img = container.querySelector('img');
      if (img) img.style.cursor = 'none';
    });
    container.addEventListener('mouseleave', () => {
      this.hideMagnifier(glass);
      // Restore cursor when magnifier is hidden
      const img = container.querySelector('img');
      if (img) img.style.cursor = '';
    });

    // Window resize handler
    this.resizeHandlers.set(img, recalcMagnifier);
    window.addEventListener('resize', recalcMagnifier);

    // Initial calculation
    recalcMagnifier();
  }

  createMagnifierGlass(img) {
    const glass = document.createElement("div");
    glass.className = "img-magnifier-glass";
    
    // Set initial styles
    Object.assign(glass.style, {
      position: 'absolute',
      backgroundImage: `url('${img.src}')`,
      backgroundRepeat: 'no-repeat',
      visibility: 'hidden',
      opacity: '0',
      transition: 'opacity 0.2s ease',
      pointerEvents: 'none',
      zIndex: '1000'
    });

    return glass;
  }

  moveMagnifier(e, img, glass) {
    e.preventDefault();
    
    const pos = this.getCursorPos(e, img);
    const w = glass.offsetWidth / 2;
    const h = glass.offsetHeight / 2;

    // Constrain position within image bounds
    let x = Math.max(w / this.zoom, Math.min(pos.x, img.width - (w / this.zoom)));
    let y = Math.max(h / this.zoom, Math.min(pos.y, img.height - (h / this.zoom)));

    // Position magnifier
    glass.style.left = `${x - w}px`;
    glass.style.top = `${y - h}px`;

    // Set background position
    const bgX = -((x * this.zoom) - w + this.borderWidth);
    const bgY = -((y * this.zoom) - h + this.borderWidth);
    glass.style.backgroundPosition = `${bgX}px ${bgY}px`;
  }

  getCursorPos(e, img) {
    const rect = img.getBoundingClientRect();
    const x = (e.pageX || e.touches?.[0]?.pageX || 0) - rect.left - window.pageXOffset;
    const y = (e.pageY || e.touches?.[0]?.pageY || 0) - rect.top - window.pageYOffset;
    return { x, y };
  }

  recalculateMagnifier(img, glass) {
    const newWidth = img.width * this.zoom;
    const newHeight = img.height * this.zoom;
    glass.style.backgroundSize = `${newWidth}px ${newHeight}px`;
  }

  showMagnifier(glass) {
    glass.style.visibility = 'visible';
    glass.style.opacity = '1';
  }

  hideMagnifier(glass) {
    glass.style.opacity = '0';
    // Use a timeout to hide after transition
    setTimeout(() => {
      if (glass.style.opacity === '0') {
        glass.style.visibility = 'hidden';
      }
    }, 200);
  }

  setupFirstMoveHandler() {
    this.firstMoveHandler = (e) => {
      document.removeEventListener('mousemove', this.firstMoveHandler);
      
      document.querySelectorAll('.splide--product image-with-placeholder').forEach(container => {
        const magnifier = container.querySelector('.img-magnifier-glass');
        if (!magnifier) return;

        const rect = container.getBoundingClientRect();
        const isMouseOver = e.clientX >= rect.left && e.clientX <= rect.right &&
                           e.clientY >= rect.top && e.clientY <= rect.bottom;
        
        if (isMouseOver) {
          this.showMagnifier(magnifier);
        }
      });
    };
    
    document.addEventListener('mousemove', this.firstMoveHandler, { once: true });
  }

  cleanup() {
    // Remove existing magnifier glasses
    document.querySelectorAll('.img-magnifier-glass').forEach(glass => glass.remove());
    
    // Remove resize handlers
    this.resizeHandlers.forEach((handler, img) => {
      window.removeEventListener('resize', handler);
    });
    
    // Remove first move handler if it exists
    if (this.firstMoveHandler) {
      document.removeEventListener('mousemove', this.firstMoveHandler);
    }
    
    // Clear tracking sets and maps
    this.initialized.clear();
    this.resizeHandlers.clear();
    this.firstMoveHandler = null;
  }

  // Public method to update zoom level
  updateZoom(newZoom) {
    this.zoom = newZoom;
    // Recalculate all existing magnifiers
    document.querySelectorAll('.img-magnifier-glass').forEach(glass => {
      const img = glass.nextElementSibling;
      if (img && img.tagName === 'IMG') {
        this.recalculateMagnifier(img, glass);
      }
    });
  }

  // Destroy the magnifier instance completely
  destroy() {
    this.cleanup();
  }
}

// Usage functions for backward compatibility and ease of use
function magnifyByClass(className, zoom = 2) {
  const magnifier = new ImageMagnifier({ zoom });
  const selector = className ? `.${className}` : 'image-with-placeholder';
  magnifier.init(selector);
  return magnifier; // Return instance for further control
}

// Improved initialization for Shopify product pages
function initProductPageMagnifier() {
  const info = document.getElementById('shopify-page-info');
  const template = info?.dataset?.template;

  if (template === 'product') {
    console.log('Initializing Product Page Magnifier');
    
    const magnifier = new ImageMagnifier({ zoom: 2 });
    
    // Initial setup
    magnifier.init('.splide--product image-with-placeholder');
    
    // Handle dynamically loaded images
    const imageObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const newImages = node.querySelectorAll ? 
              node.querySelectorAll('image-with-placeholder img') : [];
            
            if (newImages.length > 0) {
              // Re-initialize magnifier for new images
              setTimeout(() => magnifier.init('.splide--product image-with-placeholder'), 100);
            }
          }
        });
      });
    });
    
    // Observe the document for changes
    imageObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
    
    // Handle image load events more efficiently
    document.addEventListener('load', (e) => {
      if (e.target.tagName === 'IMG' && 
          e.target.closest('image-with-placeholder')) {
        setTimeout(() => magnifier.init('.splide--product image-with-placeholder'), 50);
      }
    }, true); // Use capture phase
    
    return magnifier;
  }
}

// Auto-initialize if DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductPageMagnifier);
} else {
  initProductPageMagnifier();
}